import React, { ReactElement } from 'react';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { ModalAiProductsVariantProps, SharedModalProps } from 'types/Modal';
import { useAiSubscriptionCheckout } from 'hooks/api/backend/ai-subscription/useAiSubscriptionCheckout';
import { ProtectedRoutes } from 'types/Routes';
import { AxiosError } from 'axios';
import { ToastVariant } from 'types/Toast';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import {
  AiSubscriptionActionType,
  AiSubscriptionProduct,
  PendingChange,
  PendingChangeVariant,
} from 'types/AiSubscription';
import { formattedDate } from 'utils/formattedDate';
import { AiProduct } from './AiProduct';
import { CustomModal } from '../../CustomModal';
import { CustomModalContent } from '../../CustomModalContent';

type Props = SharedModalProps & ModalAiProductsVariantProps;

const getPendingChangeDetails = (
  pendingChange: PendingChange | undefined,
  product: AiSubscriptionProduct,
  isSubExpiring: boolean
): string | undefined => {
  if (isSubExpiring && product.actionType === AiSubscriptionActionType.DOWNGRADE) {
    return `Dein Novaheal-Abo läuft in weniger als einem Monat ab. Ein Änderung am Plan würde erst in Kraft treten, wenn dein Abo schon beendet ist. Bitte verlängere dein Abo, um fortzufahren.`;
  }

  if (!pendingChange) return undefined;

  if (
    pendingChange?.variant === PendingChangeVariant.CANCELLATION &&
    product.actionType === AiSubscriptionActionType.CURRENT
  ) {
    return `Dein Abonnement ist zum ${formattedDate(pendingChange.effectiveDate)} gekündigt.`;
  }
  if (
    pendingChange?.variant === PendingChangeVariant.DOWNGRADE &&
    product.actionType === AiSubscriptionActionType.DOWNGRADE
  ) {
    return `Dein Abonnement wird zum ${formattedDate(pendingChange.effectiveDate)} auf ${pendingChange.targetTier.targetTierName} heruntergestuft.`;
  }
  return undefined;
};

export const AiProductsModal = ({
  isShown,
  handleClose: closeModal,
  products,
  pendingChange,
  isSubExpiring,
}: Props): ReactElement => {
  const { show } = useNovaToast();
  const handleClose = (): void => {
    if (closeModal) closeModal();
  };

  const checkoutMutation = useAiSubscriptionCheckout();

  const handleSelectPlan = async (tierId: string): Promise<void> => {
    try {
      const checkoutData = await checkoutMutation.mutateAsync({
        tierId,
        successUrl: `${window.location.origin}${ProtectedRoutes.AI_SUBSCRIPTION_REDIRECT}?checkout=success`,
        cancelUrl: `${window.location.origin}${ProtectedRoutes.AI_SUBSCRIPTION_REDIRECT}?checkout=cancel`,
        portalReturnUrl: `${window.location.origin}${ProtectedRoutes.AI_SUBSCRIPTION_MANAGEMENT}`,
      });

      // Redirect to Stripe checkout
      window.location.href = checkoutData.checkoutUrl;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 403) {
          show({
            title: 'KI-Pakete sind nur mit Abo verfügbar.',
            description: 'Du brauchst den vollen NOVAHEAL-Zugang, um KI-Pakete zu nutzen.',
            variant: ToastVariant.ERROR,
          });
          return;
        }
      }
      show({
        title: 'Fehler beim Erstellen des Checkouts',
        description: 'Bitte versuche es später erneut.',
        variant: ToastVariant.ERROR,
      });
    }
  };

  return (
    <CustomModal isOpen={isShown} onClose={handleClose} placement="center" size="lg">
      <CustomModalContent p={{ base: '15px 20px 40px', md: '25px 20px' }} scrollbar="hidden">
        <VStack gap="4xl">
          <VStack gap="l">
            <Text textStyle="h3.medium">KI-Pakete</Text>
            <Text textStyle="body.m.regular" color="text.tertiary">
              Wähle dein Paket für KI-Arbeitsblätter.
            </Text>
          </VStack>
          <VStack gap="15px">
            <HStack gap="15px" width="100%">
              {products?.map((product) => (
                <AiProduct
                  key={product.id}
                  product={product}
                  onSelectPlan={handleSelectPlan}
                  pendingChangeDetails={getPendingChangeDetails(
                    pendingChange,
                    product,
                    isSubExpiring ?? false
                  )}
                  disabled={
                    product.actionType === AiSubscriptionActionType.CURRENT ||
                    (product.actionType === AiSubscriptionActionType.DOWNGRADE &&
                      pendingChange?.variant === PendingChangeVariant.DOWNGRADE) ||
                    (isSubExpiring && product.actionType === AiSubscriptionActionType.DOWNGRADE)
                  }
                />
              ))}
            </HStack>
            <HStack
              borderRadius="10px"
              border="default"
              borderColor="border.accent"
              p="15px"
              gap="18px"
            >
              <VStack gap="xs" alignItems="flex-start">
                <Text textStyle="body.s.medium">Team Paket</Text>
                <Text textStyle="body.s.regular" color="text.secondary">
                  Kontaktiere uns, wenn ihr KI-Arbeitsblätter im Team nutzen möchtet.
                </Text>
              </VStack>
              <Button as="a" variant="primary" minWidth="220px">
                <a href="mailto:contact@novaheal.de?subject=KI-Zugang für meine Schule">
                  Kontakt aufnehmen
                </a>
              </Button>
            </HStack>
          </VStack>
        </VStack>
      </CustomModalContent>
    </CustomModal>
  );
};
